import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from './useDebounce';
import { getSuggestedEmail } from '../lib/emailValidator';

export const useEmailTypoSuggestion = ({ inputRef, debounceMs = 1000 }) => {
  const [suggestion, setSuggestion] = useState(null);

  const checkForTypos = useCallback((value) => {
    if (!value) {
      setSuggestion(null);
      return;
    }
    const result = getSuggestedEmail(value);
    setSuggestion(result?.suggested !== value ? result : null);
  }, []);

  const debouncedCheck = useDebounce(checkForTypos, debounceMs);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleInput = (e) => {
      e.target.setCustomValidity('');
      debouncedCheck(e.target.value);
    };

    const handleBlur = (e) => {
      debouncedCheck.cancel();
      checkForTypos(e.target.value);
    };

    input.addEventListener('input', handleInput);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('blur', handleBlur);
    };
  }, [inputRef, checkForTypos, debouncedCheck]);

  const applySuggestion = useCallback(() => {
    const input = inputRef.current;
    if (!input || !suggestion) return;

    debouncedCheck.cancel();
    input.value = suggestion.suggested;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.focus();
    input.setCustomValidity('');
    setSuggestion(null);
  }, [inputRef, suggestion, debouncedCheck]);

  const dismissSuggestion = useCallback(() => setSuggestion(null), []);

  return { suggestion, applySuggestion, dismissSuggestion };
};
